import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Icon } from '@iconify/react'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useAuthStore } from '@/stores/auth'

const loginSchema = z.object({
  email: z.string().email('กรุณากรอกอีเมลที่ถูกต้อง'),
  password: z.string().min(1, 'กรุณากรอกรหัสผ่าน'),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginPage() {
  const navigate = useNavigate()
  const { login, isLoading, error, clearError } = useAuthStore()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (data: LoginFormData) => {
    clearError()
    const success = await login(data.email, data.password)
    if (success) {
      navigate('/tenders')
    }
  }

  return (
    <AuthLayout>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-display text-2xl font-bold text-slate-900">
            ยินดีต้อนรับกลับ
          </h1>
          <p className="mt-2 text-slate-500">
            ลงชื่อเข้าใช้เพื่อเข้าถึงแดชบอร์ดประกาศจัดซื้อจัดจ้าง
          </p>
        </div>

        {/* Error alert */}
        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-lg flex items-start gap-3">
            <Icon
              icon="solar:danger-circle-line-duotone"
              className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5"
            />
            <div>
              <p className="text-sm font-medium text-rose-800">
                การเข้าสู่ระบบล้มเหลว
              </p>
              <p className="text-sm text-rose-600">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <Input
            label="อีเมล"
            type="email"
            placeholder="you@example.com"
            icon="solar:letter-line-duotone"
            error={errors.email?.message}
            {...register('email')}
          />

          <Input
            label="รหัสผ่าน"
            type="password"
            placeholder="กรอกรหัสผ่านของคุณ"
            icon="solar:lock-password-line-duotone"
            error={errors.password?.message}
            {...register('password')}
          />

          <Button
            type="submit"
            size="lg"
            className="w-full"
            loading={isLoading}
          >
            เข้าสู่ระบบ
          </Button>
        </form>

        {/* Help text */}
        <div className="mt-6 pt-6 border-t border-slate-100 text-center">
          <p className="text-sm text-slate-500">
            มีปัญหาในการเข้าสู่ระบบ?{' '}
            <a
              href="mailto:support@g-wiz.app"
              className="font-medium text-blue-600 hover:text-blue-700"
            >
              ติดต่อฝ่ายสนับสนุน
            </a>
          </p>
        </div>
      </div>

      {/* Demo notice */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl">
        <div className="flex items-start gap-3">
          <Icon
            icon="solar:info-circle-line-duotone"
            className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
          />
          <div>
            <p className="text-sm font-medium text-blue-800">
              เฉพาะผู้ดูเท่านั้น
            </p>
            <p className="text-sm text-blue-600 mt-1">
              แอปพลิเคชันนี้สำหรับผู้ดูที่ได้รับอนุญาต บัญชีผู้ปฏิบัติงานและนักวิเคราะห์ใช้ระบบการยืนยันตัวตนแยกต่างหาก
            </p>
          </div>
        </div>
      </div>
    </AuthLayout>
  )
}
